import InitApi from "api/InitApi";
import ServerErrors from "components/SeverErrors";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import useTokenStore from "store/tokenStore";

const updateNotificationPayload = (apiResponse, notificationId) => {
	if (!apiResponse?.data || !Array.isArray(apiResponse.data.records)) {
		return apiResponse;
	}

	const records = apiResponse.data.records;
	let didChange = false;

	const nextRecords = records
		.map((record) => {
			if (record.id !== notificationId || record.was_read) {
				return record;
			}

			didChange = true;
			return {
				...record,
				was_read: true,
			};
		})
		.filter((record) => {
			const isUnreadList = apiResponse?.__queryParams?.status === "unread";
			const isRecentUnreadList = apiResponse?.__queryParams?.recent === true;

			if (isUnreadList || isRecentUnreadList) {
				return !record.was_read;
			}

			return true;
		});

	if (!didChange) {
		return apiResponse;
	}

	const currentUnread = Number(apiResponse.data.unread_count ?? 0);
	const currentRead = Number(apiResponse.data.read_count ?? 0);

	return {
		...apiResponse,
		data: {
			...apiResponse.data,
			records: nextRecords,
			unread_count: currentUnread > 0 ? currentUnread - 1 : 0,
			read_count: currentRead + 1,
		},
	};
};

const withQueryMeta = (queryKey, apiResponse) => {
	if (!Array.isArray(queryKey) || !apiResponse) {
		return apiResponse;
	}

	return {
		...apiResponse,
		__queryParams: queryKey[1] || {},
	};
};

const useMarkNotificationAsRead = (notificationId) => {
	const queryClient = useQueryClient();
	const { accessToken } = useTokenStore();

	const markAsRead = async (data) => {
		const instance = await InitApi({ accessToken });
		const id = notificationId ?? data?.id;
		const res = await instance.patch(`notifications/read/${id}`);
		return res.data;
	};

	return useMutation(markAsRead, {
		onMutate: async (payload) => {
			const targetId = notificationId ?? payload?.id;
			await queryClient.cancelQueries(["notifications"]);

			const previousQueries = queryClient.getQueriesData(["notifications"]);

			previousQueries.forEach(([queryKey, queryData]) => {
				const normalized = withQueryMeta(queryKey, queryData);
				const nextValue = updateNotificationPayload(normalized, targetId);
				if (nextValue?.__queryParams) {
					delete nextValue.__queryParams;
				}
				queryClient.setQueryData(queryKey, nextValue);
			});

			return { previousQueries };
		},
		onError: async (error, _payload, context) => {
			if (context?.previousQueries) {
				context.previousQueries.forEach(([queryKey, queryData]) => {
					queryClient.setQueryData(queryKey, queryData);
				});
			}

			toast.error(
				<ServerErrors
					errorMessage={error?.response?.data?.message}
					errors={error?.response?.data?.data}
				/>,
				{
					className: "max-w-max",
				}
			);
		},
		onSettled: async () => {
			await queryClient.invalidateQueries(["notifications"]);
		},
	});
};

export default useMarkNotificationAsRead;
